import React, { useRef, useLayoutEffect, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PageWrapper from './PageWrapper';
import EditableText from './EditableText';
import { appendixScaleCells } from './AppendixTrendMarkers';
import { RaterIcon, buildIconColorMap } from './PaginatedPrioritySection';
import { selectPageSlice } from '../Selector';
import { reportPageCount } from '../../../redux/actions';

const PAGE_HEIGHT = 1123;
const PADDING_TOP = 64;
const PADDING_BOTTOM = 60;
const FOOTER_HEIGHT = 40;
const SAFETY_BUFFER = 4;
const CONTENT_HEIGHT = PAGE_HEIGHT - PADDING_TOP - PADDING_BOTTOM - FOOTER_HEIGHT - SAFETY_BUFFER; // 969px
const CONTENT_WIDTH = 794 - 60 - 60; // 674px

const SCALE_HEADERS = ['Never', 'Rarely', 'Occasion- ally', 'Sometimes', 'Usually', 'Always'];

// Rater legend shown under the intro note. Swatches reuse RaterIcon with the
// same colour map as the table markers so the legend matches exactly.
const LEGEND = [
  { letter: 'S', label: 'Self' },
  { letter: 'M', label: 'Manager' },
  { letter: 'P', label: 'Peer' },
  { letter: 'T', label: 'Team Member' },
  { letter: 'O', label: 'Other' },
];
const LEGEND_COLOR_MAP = buildIconColorMap(LEGEND.map(({ letter }) => ({ letter })));

// Split "PILLAR - COMPETENCY$$**Behavior" the same way the priority table does.
function parseRow(row) {
  const combined = String(row[0] || '');
  const [pillarRaw, behaviorRaw = ''] = combined.split('$$**');
  return {
    pillar: (pillarRaw || '').trim(),
    behavior: (behaviorRaw || '').trim(),
    scalePositions: [row[1], row[2], row[3], row[4], row[5], row[6]].map((c) => (Array.isArray(c) ? c : [])),
  };
}

function AppendixThead() {
  return (
    <thead>
      <tr>
        <th className="col-category">
          <div className="col-category__top">Lever</div>
          <div className="col-category__bottom">Priority Behaviors</div>
        </th>
        {SCALE_HEADERS.map((label, idx) => (
          <th key={idx} className="col-scale">{label}</th>
        ))}
      </tr>
    </thead>
  );
}

function AppendixRow({ row }) {
  const { pillar, behavior, scalePositions } = parseRow(row);
  return (
    <tr>
      <td className="col-category">
        <div className="col-category__pillar">{pillar}</div>
        {behavior && <div className="col-category__behavior">{behavior}</div>}
      </td>
      {appendixScaleCells(scalePositions)}
    </tr>
  );
}

function AppendixTrendPage({ data, personName, startPageNumber, slice, reportPageCount }) {
  const rows = (data.tableContents && data.tableContents.contents) || [];

  const measureRef = useRef(null);
  const [pageRowGroups, setPageRowGroups] = useState(null);

  const isFirstSliceRef = useRef(true);
  useEffect(() => {
    if (isFirstSliceRef.current) {
      isFirstSliceRef.current = false;
      return;
    }
    setPageRowGroups(null);
  }, [slice]);

  useLayoutEffect(() => {
    if (pageRowGroups) return;
    if (!measureRef.current) return;
    const container = measureRef.current;

    const rect = (sel) => {
      const el = container.querySelector(sel);
      return el ? el.getBoundingClientRect().height : 0;
    };
    const headerH = rect('[data-ax="header"]');
    const theadH = rect('[data-ax="thead"]');
    const trEls = Array.from(container.querySelectorAll('tr[data-ax-row]'));
    const rowHs = trEls.map((tr) => tr.getBoundingClientRect().height);

    // Fill each page with rows until the next row would overflow, then split to
    // a new page (repeating the thead). Page 1 also reserves the header height.
    const page1Avail = CONTENT_HEIGHT - headerH - theadH;
    const restAvail = CONTENT_HEIGHT - theadH;

    const allPages = [];
    let pageRows = [];
    let usedH = 0;
    let isFirstPage = true;

    rows.forEach((row, idx) => {
      const h = rowHs[idx] || 0;
      const avail = isFirstPage ? page1Avail : restAvail;
      if (pageRows.length > 0 && usedH + h > avail) {
        allPages.push(pageRows);
        pageRows = [row];
        usedH = h;
        isFirstPage = false;
      } else {
        pageRows.push(row);
        usedH += h;
      }
    });
    if (pageRows.length > 0) allPages.push(pageRows);

    const groups = allPages.length > 0 ? allPages : [rows];
    setPageRowGroups(groups);
    if (reportPageCount) reportPageCount('appendixForApplied', groups.length);
  }, [pageRowGroups, rows, reportPageCount]);

  const header = (
    <>
      <h2 className="pdf-section-heading pdf-section-heading--numbered">
        <span className="pdf-section-heading__badge">8</span>
        <EditableText
          as="span"
          path="appendixForApplied.sectionHeading"
          variant="inline"
          stripInlineWrap
          fallback="Appendix: Score shift from T1(Previous) to T2 (Latest)"
        />
      </h2>
      <EditableText
        as="p"
        className="pdf-body-text"
        path="appendixForApplied.intro"
        variant="block"
        stripInlineWrap
        fallback="Time 1 perception for each behavior is shown in the grey circles and the line connecting Time 1 with Time 2 shows how the perception has shifted over time."
      />
      <div className="appendix-legend">
        {LEGEND.map(({ letter, label }) => (
          <div key={letter} className="appendix-legend__item">
            <RaterIcon label={letter} colorMap={LEGEND_COLOR_MAP} />
            <span>{label}</span>
          </div>
        ))}
        <div className="appendix-legend__item">
          <span className="appendix-marker appendix-marker--outline" />
          <span>Black outline - No change</span>
        </div>
      </div>
    </>
  );

  // ── Phase 1: measurement ────────────────────────────────────────────────────
  if (!pageRowGroups) {
    return (
      <div style={{ position: 'relative', height: 0, overflow: 'hidden' }}>
        <div
          ref={measureRef}
          style={{ position: 'absolute', top: 0, left: 0, width: CONTENT_WIDTH, visibility: 'hidden', pointerEvents: 'none' }}
        >
          <div data-ax="header">{header}</div>
          <table className="pdf-table pdf-table--priority pdf-table--appendix-trend">
            <thead data-ax="thead">
              <tr>
                <th className="col-category">
                  <div className="col-category__top">Lever</div>
                  <div className="col-category__bottom">Priority Behaviors</div>
                </th>
                {SCALE_HEADERS.map((label, idx) => (
                  <th key={idx} className="col-scale">{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} data-ax-row={idx}>
                  <td className="col-category">
                    <div className="col-category__pillar">{parseRow(row).pillar}</div>
                    {parseRow(row).behavior && <div className="col-category__behavior">{parseRow(row).behavior}</div>}
                  </td>
                  {appendixScaleCells(parseRow(row).scalePositions)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ── Phase 2: render pages ───────────────────────────────────────────────────
  return (
    <>
      {pageRowGroups.map((pageRows, pageIdx) => (
        <PageWrapper key={pageIdx} pageNumber={startPageNumber + pageIdx} personName={personName}>
          {pageIdx === 0 && header}
          <div className="priority-table-wrapper">
            <table className="pdf-table pdf-table--priority pdf-table--appendix-trend">
              <AppendixThead />
              <tbody>
                {pageRows.map((row, rowIdx) => (
                  <AppendixRow key={rowIdx} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        </PageWrapper>
      ))}
    </>
  );
}

const mapStateToProps = (state) => ({
  slice: selectPageSlice(state, 'appendixForApplied'),
});

const mapDispatchToProps = (dispatch) => ({
  reportPageCount: (sectionKey, count) => dispatch(reportPageCount(sectionKey, count)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AppendixTrendPage);
